import { useEffect, useMemo, useState } from 'react';
import {
  listUserCategories,
  listUserWorkflows,
  type UserWorkflowDTO,
  type WorkflowCategoryDTO,
} from '../api/workflowApi';

/**
 * Pre-seed default categories shown in the picker even when the user
 * has zero workflows (or zero workflows in those categories yet).
 *
 * These mirror the existing WorkflowCategory enum from shared/types.ts
 * so users encountering the picker for the first time see familiar
 * groupings rather than a blank slate. They aren't persisted server-
 * side until a workflow is actually created under them.
 */
export const DEFAULT_CATEGORIES = [
  'Contracts',
  'Compliance',
  'Drafting',
  'Extraction',
  'General',
] as const;

export interface PickerCategory {
  name: string;
  workflowCount: number;
  /** Workflows under this category, sorted by recency. */
  workflows: UserWorkflowDTO[];
  /** True when this category exists only as a default seed (no workflows
   *  saved under it yet). UI uses this to render an empty-state hint. */
  isDefault: boolean;
}

/**
 * Hook for the WorkflowDestinationPicker. Loads:
 *   - the user's actual workflows (used to populate per-category lists)
 *   - the user's actual categories (count metadata, ground truth)
 *
 * Merges the two into a single PickerCategory[] with default seeds
 * appended for categories the user hasn't created yet. The merged list
 * preserves user-defined categories first, then seeds.
 *
 * `refresh()` re-fetches both lists. Used after a save completes to
 * surface the freshly-created workflow in the picker without a full
 * page reload.
 */
export function useWorkflowCategories(enabled: boolean): {
  categories: PickerCategory[];
  loading: boolean;
  refresh: () => Promise<void>;
} {
  const [workflows, setWorkflows] = useState<UserWorkflowDTO[]>([]);
  const [serverCats, setServerCats] = useState<WorkflowCategoryDTO[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useMemo(() => async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const [w, c] = await Promise.all([listUserWorkflows(), listUserCategories()]);
      setWorkflows(w);
      setServerCats(c);
    } catch (err) {
      console.warn('[useWorkflowCategories] fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);

  const categories = useMemo<PickerCategory[]>(() => {
    // Group workflows by their userCategory string. Workflows without a
    // category land in "General" so they're never invisible.
    const byCategory = new Map<string, UserWorkflowDTO[]>();
    for (const wf of workflows) {
      const cat = wf.userCategory?.trim() || 'General';
      const arr = byCategory.get(cat) ?? [];
      arr.push(wf);
      byCategory.set(cat, arr);
    }

    const userCategoryNames = new Set<string>();
    for (const c of serverCats) userCategoryNames.add(c.name);
    for (const cat of byCategory.keys()) userCategoryNames.add(cat);

    const out: PickerCategory[] = [];
    // User categories first, alpha-sorted for stable order.
    for (const name of [...userCategoryNames].sort()) {
      const wfs = byCategory.get(name) ?? [];
      out.push({
        name,
        workflowCount: wfs.length,
        workflows: wfs.sort((a, b) =>
          (b.lastRunAt ?? b.updatedAt).localeCompare(a.lastRunAt ?? a.updatedAt),
        ),
        isDefault: false,
      });
    }
    // Default seeds for categories the user hasn't created yet.
    for (const seed of DEFAULT_CATEGORIES) {
      if (userCategoryNames.has(seed)) continue;
      out.push({
        name: seed,
        workflowCount: 0,
        workflows: [],
        isDefault: true,
      });
    }
    return out;
  }, [workflows, serverCats]);

  return { categories, loading, refresh: fetchAll };
}
